// The project's shot board (v2): per scene, the frame, the clip and the sounds, each with its status, live job
// progress and a tap for whatever comes next. Plus the produce bar (the three pacing modes as taps) and the music
// strip. Every button here is something the Director can also do by voice, through the same endpoints.
import { useState } from "react";
import { useFlag } from "../flags";
import * as act from "../actions";
import { api } from "../api";
import { toggleAudio } from "../audio";
import { jobWasLogged, LOGGED_LINE } from "../logged";
import { bump, useStore } from "../store";
import type { Estimate, JobKind, JobView, ProduceMode, Project } from "../types";
import { RedoSheet } from "./Artifact";
import { SunoPack, SunoPackBody } from "./SunoPack";
import { fmtCredits, Icon, Sheet, Spinner, useLoad } from "./bits";
import { Download } from "./Download";
import { MoreMenu } from "./MoreMenu";
import { Notice } from "./Notice";
import { PaidButton, usePrice } from "./PriceFirst";
import { RetryButton } from "./RetryButton";

/** The job working on this scene (or the project, for music) right now, else the one that just failed. */
export function useJob(projectId: string, sceneId: string | null, kinds: JobKind[]): JobView | undefined {
  return useStore((s) => {
    let best: JobView | undefined;
    for (const j of Object.values(s.jobs)) {
      if (j.projectId !== projectId || j.sceneId !== sceneId || !kinds.includes(j.kind)) continue;
      if (j.compareId) continue; // v4 (V4-MC): a comparison's candidate isn't the scene's own frame or clip
      if (!best || j.updatedAt > best.updatedAt || (j.updatedAt === best.updatedAt && j.createdAt > best.createdAt)) best = j;
    }
    return best && best.status !== "done" ? best : undefined;
  });
}
export const active = (j?: JobView) => !!j && (j.status === "queued" || j.status === "running");

export function Pill({ tone, children }: { tone: "draft" | "busy" | "done" | "bad" | "off"; children: string }) {
  return <span className={`pill pill-${tone}`}>{children}</span>;
}

export function JobLine({ job }: { job: JobView }) {
  const pct = Math.round(job.progress * 100);
  const [busy, setBusy] = useState(false);
  // v4 (D6 parity with cancel_job / retry_job): the same tools, by tap. The job's id is the `which`.
  const tap = (tool: "cancel_job" | "retry_job", said: string) => async () => {
    setBusy(true);
    try {
      await act.tapTool(tool, { ...(job.projectId ? { project_id: job.projectId } : {}), which: job.id }, said);
      bump("project", "credits");
    } finally {
      setBusy(false);
    }
  };
  if (job.status === "failed")
    return (
      <div className="step-job is-bad" role="alert">
        <p>{job.error ?? "That didn't work."} Credits refunded.</p>
        {jobWasLogged(job) && <p className="logged-note">{LOGGED_LINE}</p>}
        {/* a clip is video, so it goes back through Animate (price first) or the tray's suggestion; PRICE-FIRST: the
            rest show their own price and send it with the retry */}
        {job.projectId && job.kind !== "clip" && job.kind !== "render" && (
          <RetryButton projectId={job.projectId} sceneId={job.sceneId} kind={job.kind} className="btn btn-quiet btn-small step-job-act" />
        )}
      </div>
    );
  return (
    <div className="step-job">
      <div className="bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={job.step ?? "Working"}>
        <span style={{ width: `${Math.max(4, pct)}%` }} />
      </div>
      <p>
        {job.status === "queued" ? "Waiting for a free slot" : (job.step ?? "Working")}{" "}
        <span className="step-job-end">
          <b>{pct}%</b>
          {active(job) && (
            <button type="button" className="step-job-stop" data-action="job.cancel" disabled={busy} onClick={tap("cancel_job", `stopped the ${job.kind} being made`)} aria-label={`Stop the ${job.kind}. Its credits come back`}>
              {busy ? "Stopping" : "Stop"}
            </button>
          )}
        </span>
      </p>
    </div>
  );
}

// ------------------------------------------------------------------ produce bar: the pacing modes as taps

// dataAction: the tap's parity id (web/src/parity.ts); the Make… menu's items carry the same ids
export const PACE: { through: ProduceMode["through"]; label: string; hint: string; dataAction: string }[] = [
  { through: "frames", label: "All frames", hint: "A still for every scene, to approve one by one", dataAction: "produce.frames" },
  { through: "clips", label: "Animate approved", hint: "A clip for every approved frame", dataAction: "produce.clips" },
  { through: "render", label: "Finish it all", hint: "Everything that's missing, then the final cut", dataAction: "produce.all" },
];

export function ProduceBar({ p }: { p: Project }) {
  const [mode, setMode] = useState<ProduceMode | null>(null);
  const running = useStore((s) => Object.values(s.jobs).filter((j) => j.projectId === p.id && (j.status === "queued" || j.status === "running")).length);
  // flag make_menu: the three pacing modes in one "Make…" menu (same sheets, same ids); the Render button stays put
  const makeMenu = useFlag("make_menu");
  if (makeMenu)
    return (
      <div className="produce produce-make" aria-label="Make the shots">
        <MoreMenu
          label="Make"
          trigger={
            <>
              <Icon name="sparkle" size={16} /> Make…
            </>
          }
          triggerClass="btn btn-quiet make-btn"
          menuClass="make-menu"
          items={PACE.map((m) => ({ id: m.through, label: m.label, hint: m.hint, icon: m.through === "frames" ? "image" : m.through === "clips" ? "film" : "bolt", dataAction: m.dataAction, onSelect: () => setMode({ scope: "all", through: m.through }) }))}
        />
        {running > 0 && (
          <span className="status status-busy">
            {running} {running === 1 ? "job" : "jobs"} running
          </span>
        )}
        {mode && <ProduceSheet p={p} mode={mode} onClose={() => setMode(null)} />}
      </div>
    );
  return (
    <div className="produce" aria-label="Make the shots">
      <div className="produce-head">
        <p className="panel-label">Make it your way</p>
        {running > 0 && (
          <span className="status status-busy">
            {running} {running === 1 ? "job" : "jobs"} running
          </span>
        )}
      </div>
      <div className="produce-row">
        {PACE.map((m) => (
          <button type="button" key={m.through} className="produce-btn" title={m.hint} data-action={m.dataAction} onClick={() => setMode({ scope: "all", through: m.through })}>
            <strong>{m.label}</strong>
            <span>{m.hint}</span>
          </button>
        ))}
      </div>
      {mode && <ProduceSheet p={p} mode={mode} onClose={() => setMode(null)} />}
    </div>
  );
}

/** The price-first confirm for a produce batch (also opened by the project screen's NextStep, V4-P1). */
export function ProduceSheet({ p, mode, onClose }: { p: Project; mode: ProduceMode; onClose: () => void }) {
  // fix-produce: the price is produce's own plan for this mode (api.estimate with a mode), the same number the NEXT
  // bar shows and the one produce checks the yes against
  const { data: loaded, error } = useLoad<Estimate>(() => api.estimate(p.id, mode), [p.id, mode.through, mode.scope, mode.sceneId]);
  const balance = useStore((s) => s.balance);
  const [going, setGoing] = useState(false);
  // a yes the server refused: a new price to ask about again, or what went wrong (the sheet stays open either way)
  const [repriced, setRepriced] = useState<{ price: Estimate; text: string } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const est = repriced?.price ?? loaded;
  const pace = PACE.find((x) => x.through === mode.through) ?? { through: mode.through, label: "Make the sounds", hint: "Sounds for every scene with a clip" };
  // video always needs a yes: say so when this batch makes clips
  const video = est ? (est.clips !== undefined ? est.clips > 0 : est.breakdown.some((l) => /clip|animat/i.test(l.label))) : false;
  const go = async () => {
    if (!est) return;
    setGoing(true);
    setFailure(null);
    const r = await act.produceConfirmed(p.id, { ...mode, confirm: true, expectCredits: est.credits });
    setGoing(false);
    if (r.kind === "ok") return onClose();
    if (r.kind === "reprice") setRepriced({ price: r.price, text: r.text });
    else setFailure(r.text);
  };
  return (
    <Sheet title={`${pace.label}?`} onClose={onClose}>
      <div className="sheet-body confirm">
        {!est && !error && <Spinner label="Pricing it" />}
        {!est && error && <p className="form-error" role="alert">Couldn't price this. {error}</p>}
        {repriced && <Notice tone="warning" text={`${repriced.text} Go ahead?`} code="CONFIRM_REQUIRED" />}
        {est && (
          <>
            <p className="confirm-num">
              {fmtCredits(est.credits)} <span>credits</span>
            </p>
            <ul className="est-lines">
              {est.breakdown.map((l) => (
                <li key={l.label}>
                  <span>
                    {l.label}
                    {l.detail && <small>{l.detail}</small>}
                  </span>
                  <span>{fmtCredits(l.credits)}</span>
                </li>
              ))}
            </ul>
            <p className="muted">
              {pace.hint}. {video ? "This includes video, so it needs your yes. " : ""}You have {fmtCredits(balance)}. If anything fails, its credits come back.
            </p>
          </>
        )}
        {failure && <Notice tone="error" text={`Couldn't start it. ${failure}`} />}
        <div className="confirm-actions">
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Not yet
          </button>
          <button data-action="produce.go" type="button" className="btn btn-primary" disabled={!est || going || est.credits === 0} data-autofocus onClick={() => void go()}>
            {going ? "Starting" : est?.credits === 0 ? "Nothing to make" : failure ? `Try again for ${fmtCredits(est?.credits ?? 0)}` : `Go for ${fmtCredits(est?.credits ?? 0)}`}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

// ------------------------------------------------------------------ music strip

export function MusicStrip({ p }: { p: Project }) {
  const job = useJob(p.id, null, ["music"]);
  const playing = useStore((s) => s.playing === `music-${p.id}`);
  const [redo, setRedo] = useState(false);
  const [busy, setBusy] = useState(false);
  // v4 (V4-M1): "I'll make it in Suno" instead of generating the bed
  const [suno, setSuno] = useState(false);
  const m = p.music;
  const working = active(job);
  // PRICE-FIRST: the bed's own price, on the button and sent with the tap
  const price = usePrice(p.id, "music", null, [p.brief.durationS]);
  return (
    <section className="music-strip" aria-labelledby="music-h">
      <button type="button" className={`music-play${playing ? " is-on" : ""}`} disabled={!m?.url || working} onClick={() => toggleAudio(`music-${p.id}`, m?.url)} aria-label={playing ? "Pause the music bed" : "Play the music bed"}>
        <Icon name={playing ? "pause" : m ? "play" : "music"} size={20} />
      </button>
      <div className="music-text">
        <h2 id="music-h">
          Music bed {working ? <Pill tone="busy">Composing</Pill> : m ? <Pill tone={m.status === "approved" ? "done" : "draft"}>{m.status === "approved" ? "Approved" : "Draft"}</Pill> : <Pill tone="off">Not made</Pill>}
        </h2>
        <p>{m?.prompt || p.script?.musicMood || "Instrumental, under the voiceover"}</p>
        {job && <JobLine job={job} />}
      </div>
      {!working && (
        <div className="music-acts">
          {m?.status === "draft" && (
            <button data-action="music.approve" type="button" className="btn btn-primary btn-small" disabled={busy} onClick={() => void act.approveMusic(p.id)}>
              <Icon name="check" size={14} /> Keep it
            </button>
          )}
          {m && <Download url={m.downloadUrl} label="the music bed" />}
          {m && p.suno?.assetId === m.assetId ? null : m ? (
            <button data-action="music.redo" type="button" className="icon-btn" aria-label="Redo the music bed" title="Redo" disabled={busy} onClick={() => setRedo(true)}>
              <Icon name="redo" size={18} />
            </button>
          ) : (
            <>
              <PaidButton
                price={price}
                label="Make music"
                dataAction="music.make"
                disabled={busy || !p.script}
                ask={{ title: "Make the music bed?", lede: `An instrumental bed for the whole video${p.script?.musicMood ? `: ${p.script.musicMood}` : ""}.`, go: "Make it" }}
                run={(credits) => act.startStep(p.id, "music", { ...(p.script?.musicMood ? { prompt: p.script.musicMood } : {}) }, credits)}
              />
              {!p.suno && !suno && (
                <button data-action="music.suno" type="button" className="btn btn-quiet btn-small" onClick={() => setSuno(true)}>
                  I'll make it in Suno
                </button>
              )}
            </>
          )}
        </div>
      )}
      {(p.suno || suno) && <div className="music-suno">{p.suno ? <SunoPack p={p} /> : <SunoPackBody p={p} />}</div>}
      {redo && (
        <RedoSheet
          a={{ kind: "audio", projectId: p.id, title: "Music bed" }}
          onClose={() => setRedo(false)}
          credits={price === undefined ? undefined : (price?.credits ?? null)}
          onRedo={async (note, expectCredits) => {
            setBusy(true);
            try {
              return await act.startStep(p.id, "music", { redo: true, note }, expectCredits);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </section>
  );
}
